import { AccountApi as OriginAccountApi } from '@diritto/common/api/AccountApi';
import { AccountDeviceApi as OriginAccountDeviceApi } from '@diritto/common/api/AccountDeviceApi';
import { ActivityPointApi as OriginActivityPointApi } from '@diritto/common/api/ActivityPointApi';
import { AttendanceCheckApi as OriginAttendanceCheckApi } from '@diritto/common/api/AttendanceCheckApi';
import { ContentAuthorReplyApi as OriginContentAuthorReplyApi } from '@diritto/common/api/ContentAuthorReplyApi';
import { ContentNotiApi as OriginContentNotiApi } from '@diritto/common/api/ContentNotiApi';
import { ContestApi as OriginContestApi } from '@diritto/common/api/ContestApi';
import { CouponCatalogApi as OriginCouponCatalogApi } from '@diritto/common/api/CouponCatalogApi';
import { DrmFontApi as OriginDrmFontApi } from '@diritto/common/api/DrmFontApi';
import { EditorApi as OriginEditorApi } from '@diritto/common/api/EditorApi';
import { EpisodeReadRecordApi as OriginEpisodeReadRecordApi } from '@diritto/common/api/EpisodeReadRecordApi';
import { EpisodeReplyApi as OriginEpisodeReplyApi } from '@diritto/common/api/EpisodeReplyApi';
import { EpisodeViewApi as OriginEpisodeViewApi } from '@diritto/common/api/EpisodeViewApi';
import { EventApi as OriginEventApi } from '@diritto/common/api/EventApi';
import { FancyShareApi as OriginFancyShareApi } from '@diritto/common/api/FancyShareApi';
import { FavoriteContentApi as OriginFavoriteContentApi } from '@diritto/common/api/FavoriteContentApi';
import { NotiApi as OriginNotiApi } from '@diritto/common/api/NotiApi';
import { NpsCampaignApi as OriginNpsCampaignApi } from '@diritto/common/api/NpsCampaignApi';
import { PartnerApi as OriginPartnerApi } from '@diritto/common/api/PartnerApi';
import { ProfileApi as OriginProfileApi } from '@diritto/common/api/ProfileApi';
import { ProfileBlockApi as OriginProfileBlockApi } from '@diritto/common/api/ProfileBlockApi';
import { ProfileFollowApi as OriginProfileFollowApi } from '@diritto/common/api/ProfileFollowApi';
import { ProfileReplyApi as OriginProfileReplyApi } from '@diritto/common/api/ProfileReplyApi';
import { ProfileReportApi as OriginProfileReportApi } from '@diritto/common/api/ProfileReportApi';
import { PromotionApi as OriginPromotionApi } from '@diritto/common/api/PromotionApi';
import { PromotionReplyApi as OriginPromotionReplyApi } from '@diritto/common/api/PromotionReplyApi';
import { RecentlyReadApi as OriginRecentlyReadApi } from '@diritto/common/api/RecentlyReadApi';
import { RecommendEpisodeApi as OriginRecommendEpisodeApi } from '@diritto/common/api/RecommendEpisodeApi';
import { RelayApi as OriginRelayApi } from '@diritto/common/api/RelayApi';
import { RelayContentApi as OriginRelayContentApi } from '@diritto/common/api/RelayContentApi';
import { RelayEpisodeApi as OriginRelayEpisodeApi } from '@diritto/common/api/RelayEpisodeApi';
import { RelayReplyApi as OriginRelayReplyApi } from '@diritto/common/api/RelayReplyApi';
import { RelayRollingApi as OriginRelayRollingApi } from '@diritto/common/api/RelayRollingApi';
import { ReplyApi as OriginReplyApi } from '@diritto/common/api/ReplyApi';
import { RollingApi as OriginRollingApi } from '@diritto/common/api/RollingApi';
import { RollingReactionApi as OriginRollingReactionApi } from '@diritto/common/api/RollingReactionApi';
import { SearchApi as OriginSearchApi } from '@diritto/common/api/SearchApi';
import { SectionApi as OriginSectionApi } from '@diritto/common/api/SectionApi';
import { ShortUrlApi as OriginShortUrlApi } from '@diritto/common/api/ShortUrlApi';
import { SystemApi as OriginSystemApi } from '@diritto/common/api/SystemApi';
import { WsContentApi as OriginWsContentApi } from '@diritto/common/api/WsContentApi';
import { WsEpisodeApi as OriginWsEpisodeApi } from '@diritto/common/api/WsEpisodeApi';
import { Http, TokenPayload } from '@diritto/common/utils/requester/http';
import { HttpStatus } from '@diritto/common/utils/requester/httpStatus';
import { EventService, EventType } from 'libs/EventService';
import { ChatApi as OriginChatApi } from '@diritto/common/api/ChatApi';
import { ChatPartnerApi as OriginChatPartnerApi } from '@diritto/common/api/ChatPartnerApi';
import { configure } from 'utils/configure';

class PrivateApiHelper {
  // eslint-disable-next-line class-methods-use-this
  get url() {
    if (typeof window === 'undefined') {
      return configure.apiUrl;
    }
    return configure.apiPublicUrl;
  }

  private readonly http = new Http({
    refreshTokenUrl: `${this.url}/accounts/auth/refresh-token`,
    interceptorError: (error: any) => {
      switch (error.response.status) {
        case HttpStatus.SERVICE_UNAVAILABLE:
          EventService.emit(EventType.ServiceUnavailable);
          return null;
        default:
          return Promise.reject(error);
      }
    },
  });

  initApi<T>(Class: new (url: string, http: Http) => T): T {
    return new Class(`${this.url}`, this.http);
  }

  updateToken(token: TokenPayload) {
    this.http.setToken(token);
  }

  async updateConfig(callback: (http: Http) => Promise<void>) {
    await callback(this.http);
  }
}

export const ApiHelper = new PrivateApiHelper();

export const AccountApi = ApiHelper.initApi(OriginAccountApi);
export const AccountDeviceApi = ApiHelper.initApi(OriginAccountDeviceApi);
export const AttendanceCheckApi = ApiHelper.initApi(OriginAttendanceCheckApi);
export const ActivityPointApi = ApiHelper.initApi(OriginActivityPointApi);
export const ContentAuthorReplyApi = ApiHelper.initApi(OriginContentAuthorReplyApi);
export const ContentNotiApi = ApiHelper.initApi(OriginContentNotiApi);
export const CouponCatalogApi = ApiHelper.initApi(OriginCouponCatalogApi);
export const DrmFontApi = ApiHelper.initApi(OriginDrmFontApi);
export const EditorApi = ApiHelper.initApi(OriginEditorApi);
export const EpisodeReadRecordApi = ApiHelper.initApi(OriginEpisodeReadRecordApi);
export const EpisodeReplyApi = ApiHelper.initApi(OriginEpisodeReplyApi);
export const EpisodeViewApi = ApiHelper.initApi(OriginEpisodeViewApi);
export const EventApi = ApiHelper.initApi(OriginEventApi);
export const FancyShareApi = ApiHelper.initApi(OriginFancyShareApi);
export const FavoriteContentApi = ApiHelper.initApi(OriginFavoriteContentApi);
export const NotiApi = ApiHelper.initApi(OriginNotiApi);
export const NpsCampaignApi = ApiHelper.initApi(OriginNpsCampaignApi);
export const PartnerApi = ApiHelper.initApi(OriginPartnerApi);
export const ProfileApi = ApiHelper.initApi(OriginProfileApi);
export const ProfileBlockApi = ApiHelper.initApi(OriginProfileBlockApi);
export const ProfileFollowApi = ApiHelper.initApi(OriginProfileFollowApi);
export const ProfileReplyApi = ApiHelper.initApi(OriginProfileReplyApi);
export const ProfileReportApi = ApiHelper.initApi(OriginProfileReportApi);
export const PromotionApi = ApiHelper.initApi(OriginPromotionApi);
export const PromotionReplyApi = ApiHelper.initApi(OriginPromotionReplyApi);
export const RecentlyReadApi = ApiHelper.initApi(OriginRecentlyReadApi);
export const RecommendEpisodeApi = ApiHelper.initApi(OriginRecommendEpisodeApi);
export const ShortUrlApi = ApiHelper.initApi(OriginShortUrlApi);
export const RelayApi = ApiHelper.initApi(OriginRelayApi);
export const RelayContentApi = ApiHelper.initApi(OriginRelayContentApi);
export const RelayEpisodeApi = ApiHelper.initApi(OriginRelayEpisodeApi);
export const RelayReplyApi = ApiHelper.initApi(OriginRelayReplyApi);
export const RelayRollingApi = ApiHelper.initApi(OriginRelayRollingApi);
export const ReplyApi = ApiHelper.initApi(OriginReplyApi);
export const RollingApi = ApiHelper.initApi(OriginRollingApi);
export const RollingReactionApi = ApiHelper.initApi(OriginRollingReactionApi);
export const SearchApi = ApiHelper.initApi(OriginSearchApi);
export const SectionApi = ApiHelper.initApi(OriginSectionApi);
export const SystemApi = ApiHelper.initApi(OriginSystemApi);
export const WsContentApi = ApiHelper.initApi(OriginWsContentApi);
export const WsEpisodeApi = ApiHelper.initApi(OriginWsEpisodeApi);
export const ContestApi = ApiHelper.initApi(OriginContestApi);
export const ChatApi = ApiHelper.initApi(OriginChatApi);
export const ChatPartnerApi = ApiHelper.initApi(OriginChatPartnerApi);
